/**
 * Route-based persona selection for the Ask Agent widget.
 *
 * Maps URL patterns to Inkeep agent IDs so the widget automatically
 * connects to the right support persona based on where the user is.
 */

import { matchPath } from 'react-router-dom';

// Root agent ID — required by the Inkeep API middleware.
const ROOT_AGENT_ID = 'general-support';

interface PersonaConfig {
  agentId: string;
  subAgentId: string;
  label: string;
  placeholder: string;
}

const PERSONAS: Record<string, PersonaConfig> = {
  candidate: {
    agentId: ROOT_AGENT_ID,
    subAgentId: 'candidate-support',
    label: 'Support',
    placeholder: 'Hey! How can I help you today?',
  },
  recruiter: {
    agentId: ROOT_AGENT_ID,
    subAgentId: 'recruiter-help',
    label: 'Support',
    placeholder: 'Hey! Ask me anything about jobs, interviews, or candidates.',
  },
};


export function getPersonaForRoute(pathname: string): PersonaConfig | null {
  // Candidate profile (private ATS view) — hide chat entirely, matching the
  // public profile. Param names mirror the route def in new-ats/routes/index.tsx.
  // Listing (.../applications, no :applicationId) keeps the chat.
  if (
    matchPath('/ats/jobs/:jobId/applications/:applicationId/*', pathname) ||
    matchPath('/ats/jobs/:jobId/interviews/:interviewId/*', pathname)
  ) {
    return null;
  }

  // ATS platform — recruiter persona
  if (pathname.startsWith('/ats')) {
    return PERSONAS.recruiter;
  }

  // Legacy redirect — hide chat entirely
  if (/^\/jobs\/[^/]+\/stage\//.test(pathname)) {
    return null;
  }

  // Public share links — unauthenticated, no user context available
  if (
    matchPath('/public/interview/*', pathname) ||
    matchPath('/public/profile/*', pathname) ||
    matchPath('/public/assessment/*', pathname)
  ) {
    return null;
  }

  // Candidate booking / reschedule page — no support chat
  if (pathname.startsWith('/booking/')) {
    return null;
  }

  // Candidate-facing routes
  if (
    pathname === '/' ||
    pathname.startsWith('/job/') ||
    pathname.startsWith('/interview/') ||
    pathname.startsWith('/desktop-auth')
  ) {
    return PERSONAS.candidate;
  }

  // Default fallback — candidate support is safest
  return PERSONAS.candidate;
}

export const INKEEP_CONFIG = {
  // Proxied through nginx at /inkeep/ to avoid HTTPS→HTTP mixed content issues
  baseUrl: import.meta.env.VITE_INKEEP_API_URL || `${window.location.origin}/inkeep`,
};
